import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../libs/axios';
import Breadcrumb from '../../utils/breadcrumbs';
import { ListLoader } from '../../utils/loader';
import ConfirmationModal from '../../ui/confirmation-modal';
import ReopenMatchModal from '../../components/match-ops/reopen-match-modal';
import PositionsModal from '../../components/match-ops/positions-modal';
import ScoringPhoneModal from '../../components/match-ops/scoring-phone-modal';

interface LiveMatch { id: number; status: string; scorer_id: number | null; team_a_id: number; team_b_id: number; tournament_id: number | null; updated_at: string; }

// DECISION-001 S2: matches whose scoring is not reaching the server.
interface StallSignal { kind: string; summary: string; since: string }
interface StalledMatch {
  match_id: number;
  signals: StallSignal[];
  refused_not_landed: number[];
  user_ids: number[];
  first_signal_at: string;
  last_alert_at: string | null;
  match: { status: string | null; scorer_id: number | null; team_a: string | null; team_b: string | null } | null;
}
interface StalledAnswer {
  window_minutes: number;
  monitor: { enabled: boolean; email: boolean; whatsapp?: boolean };
  positions_mode: string;
  live_edit_guard: string;
  matches: StalledMatch[];
}

const SIGNAL_LABEL: Record<string, string> = {
  refused_not_landed: 'Refused balls',
  auth_rejected: 'Uploads turned away',
  drain_not_landing: 'Upload not landing',
};

const ist = (iso: string | null) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const MatchOps: React.FC = () => {
  const [matches, setMatches] = useState<LiveMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [stalled, setStalled] = useState<StalledAnswer | null>(null);
  const [stalledLoading, setStalledLoading] = useState(false);
  const [repairId, setRepairId] = useState('');
  const [reopenOpen, setReopenOpen] = useState(false);
  const [positionsOpen, setPositionsOpen] = useState(false);
  const [forceCompleteOpen, setForceCompleteOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadStalled = async () => {
    setStalledLoading(true);
    try { const res: any = await api.get('/match-ops/stalled'); setStalled(res.data || null); }
    catch (e) { toast.error('Failed to load stalled matches'); }
    finally { setStalledLoading(false); }
  };
  const load = async () => {
    setLoading(true);
    try { const res: any = await api.get('/match-ops/live'); setMatches(res.data || []); }
    catch (e) { toast.error('Failed to load live matches'); }
    finally { setLoading(false); }
    loadStalled();
  };
  useEffect(() => { load(); }, []);

  // Proves the alert paths before a real stall needs them: WhatsApp to the admin numbers
  // (ALERT_WHATSAPP_TO + the approved template) and email (ALERT_EMAIL_TO + SMTP).
  const sendTestAlert = async () => {
    setBusy(true);
    try { const res: any = await api.post('/match-ops/stall-alert-test', {}); toast.success(res.message || 'Test alert sent', { duration: 8000 }); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Test alert not sent', { duration: 8000 }); }
    finally { setBusy(false); }
  };

  const openPositions = (id?: number) => {
    const target = id != null ? String(id) : repairId;
    if (!target) { toast.error('Match ID required'); return; }
    setRepairId(target);
    setPositionsOpen(true);
  };

  const act = async (matchId: number | string, action: string, body?: any, label?: string) => {
    if (!matchId) { toast.error('Match ID required'); return; }
    setBusy(true);
    try { await api.post(`/match-ops/${matchId}/${action}`, body || {}); toast.success(label || 'Done'); load(); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Action failed'); }
    finally { setBusy(false); }
  };

  // Reopen and Force complete change a result and the points table, so neither
  // runs on a single click any more: Reopen shows a preview and asks for a reason,
  // Force complete asks for confirmation.
  const openReopen = () => {
    if (!repairId) { toast.error('Match ID required'); return; }
    setReopenOpen(true);
  };
  const openForceComplete = () => {
    if (!repairId) { toast.error('Match ID required'); return; }
    setForceCompleteOpen(true);
  };
  // DECISION-007: who holds scoring, and Move / Release (force log-out) / Clear fences,
  // each with a reason. Replaces the one-click "Clear scorer".
  const openScoringPhone = (id?: number) => {
    const target = id != null ? String(id) : repairId;
    if (!target) { toast.error('Match ID required'); return; }
    setRepairId(target);
    setPhoneOpen(true);
  };

  return (
    <div className="p-4 sm:p-6">
      <Breadcrumb />
      <div className="mt-4 mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Match Ops & Repair</h1>
        <p className="text-sm text-gray-500">Fix a drifted scorecard, reopen/complete a stuck match, or move scoring to another phone.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 max-w-3xl">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Repair by match ID</h2>
        <div className="flex flex-wrap gap-2 items-center">
          <input value={repairId} onChange={(e) => setRepairId(e.target.value.trim())} placeholder="Match ID" className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-32" />
          <button disabled={busy} onClick={() => act(repairId, 'recalculate', {}, 'Recalculated')} className="bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium px-3 py-2 rounded-lg disabled:opacity-50">Recalculate stats</button>
          <button disabled={busy} onClick={() => openPositions()} className="bg-white border border-primary-600 text-primary-700 hover:bg-primary-50 text-xs font-medium px-3 py-2 rounded-lg disabled:opacity-50">Recompute positions…</button>
          <button disabled={busy} onClick={openReopen} className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium px-3 py-2 rounded-lg disabled:opacity-50">Reopen…</button>
          <button disabled={busy} onClick={openForceComplete} className="bg-gray-700 hover:bg-gray-800 text-white text-xs font-medium px-3 py-2 rounded-lg disabled:opacity-50">Force complete…</button>
          <button disabled={busy} onClick={() => openScoringPhone()} className="bg-white border border-gray-400 text-gray-800 hover:bg-gray-50 text-xs font-medium px-3 py-2 rounded-lg disabled:opacity-50">Scoring phone…</button>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          A live match stuck on &quot;over complete&quot; refusals: <b>Recompute positions</b>, then ask the scorer to tap <b>Sync now</b> in
          Saved matches. Balls refused after a match or innings was closed: <b>Reopen</b> the match, ask the scorer to tap
          <b> Sync now</b> (<b>Try again</b> on a held ball in app 1.0.24), and let it complete by itself — or <b>Force complete</b> then
          <b> Recalculate stats</b>.
        </p>
      </div>

      <div className="flex items-center justify-between mb-3 max-w-5xl">
        <h2 className="text-sm font-semibold text-gray-900">Stalled matches</h2>
        <div className="flex items-center gap-4">
          <button onClick={sendTestAlert} disabled={busy} className="text-xs text-gray-700 hover:underline disabled:opacity-50">
            Send test alert
          </button>
          <button onClick={loadStalled} disabled={stalledLoading} className="text-xs text-primary-700 hover:underline disabled:opacity-50">
            {stalledLoading ? 'Checking…' : 'Refresh'}
          </button>
        </div>
      </div>
      {stalled && (
        <p className="text-xs text-gray-500 mb-2">
          Scoring that is not reaching the server in the last {stalled.window_minutes} minutes. Monitor {stalled.monitor.enabled ? 'on' : 'off'},
          WhatsApp alerts {stalled.monitor.whatsapp ? 'on' : 'off'}, email alerts {stalled.monitor.email ? 'on' : 'off'} · server positions {stalled.positions_mode} · live-edit guard {stalled.live_edit_guard}.
        </p>
      )}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600"><tr>
              <th className="text-left font-medium px-4 py-3">Match</th>
              <th className="text-left font-medium px-4 py-3">Problem</th>
              <th className="text-left font-medium px-4 py-3">Since (IST)</th>
              <th className="text-left font-medium px-4 py-3">Last alert</th>
              <th className="text-right font-medium px-4 py-3">Actions</th>
            </tr></thead>
            <tbody>
              {!stalled || stalled.matches.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">{stalledLoading ? 'Checking…' : 'No stalled matches'}</td></tr>
              ) : stalled.matches.map((s) => (
                <tr key={s.match_id} className="border-t border-gray-100 align-top">
                  <td className="px-4 py-3 text-gray-900">
                    #{s.match_id}
                    <div className="text-xs text-gray-500">{s.match ? `${s.match.team_a ?? 'Team A'} v ${s.match.team_b ?? 'Team B'}` : ''}</div>
                    <div className="text-xs text-gray-400 capitalize">
                      {(s.match?.status ?? '').replace(/_/g, ' ')}{s.match?.scorer_id ? ` · scorer #${s.match.scorer_id}` : ''}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <ul className="space-y-1">
                      {s.signals.map((g) => (
                        <li key={g.kind} className="text-xs text-gray-700">
                          <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-800 mr-1">{SIGNAL_LABEL[g.kind] ?? g.kind}</span>
                          {g.summary}
                        </li>
                      ))}
                    </ul>
                    {s.user_ids.length > 0 && <div className="text-xs text-gray-400 mt-1">Users: {s.user_ids.map((u) => `#${u}`).join(', ')}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{ist(s.first_signal_at)}</td>
                  <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{ist(s.last_alert_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-col items-end gap-2">
                      <button onClick={() => openPositions(s.match_id)} className="text-xs text-primary-700 hover:underline">Recompute positions…</button>
                      <button onClick={() => { setRepairId(String(s.match_id)); setReopenOpen(true); }} className="text-xs text-amber-700 hover:underline">Reopen…</button>
                      <button onClick={() => act(s.match_id, 'recalculate', {}, 'Recalculated')} className="text-xs text-gray-700 hover:underline">Recalc</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <h2 className="text-sm font-semibold text-gray-900 mb-3">Live / in-progress matches</h2>
      {loading ? <ListLoader /> : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600"><tr>
                <th className="text-left font-medium px-4 py-3">Match</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-left font-medium px-4 py-3">Scorer</th>
                <th className="text-right font-medium px-4 py-3">Actions</th>
              </tr></thead>
              <tbody>
                {matches.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No live matches</td></tr>
                ) : matches.map((m) => (
                  <tr key={m.id} className="border-t border-gray-100">
                    <td className="px-4 py-3 text-gray-900">#{m.id}<span className="text-xs text-gray-400"> · teams {m.team_a_id} v {m.team_b_id}</span></td>
                    <td className="px-4 py-3"><span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 capitalize">{m.status.replace(/_/g, ' ')}</span></td>
                    <td className="px-4 py-3 text-gray-600">{m.scorer_id ? `#${m.scorer_id}` : '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-3">
                        <button onClick={() => act(m.id, 'recalculate', {}, 'Recalculated')} className="text-xs text-primary-700 hover:underline">Recalc</button>
                        <button onClick={() => openScoringPhone(m.id)} className="text-xs text-gray-700 hover:underline">Scoring phone…</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ReopenMatchModal
        matchId={repairId}
        isOpen={reopenOpen}
        onClose={() => setReopenOpen(false)}
        onDone={load}
      />
      <PositionsModal
        matchId={repairId}
        isOpen={positionsOpen}
        onClose={() => setPositionsOpen(false)}
        onDone={loadStalled}
      />
      <ScoringPhoneModal
        matchId={repairId}
        isOpen={phoneOpen}
        onClose={() => setPhoneOpen(false)}
        onDone={load}
      />
      <ConfirmationModal
        isOpen={forceCompleteOpen}
        onClose={() => setForceCompleteOpen(false)}
        onConfirm={async () => { await act(repairId, 'force-complete', {}, 'Completed'); setForceCompleteOpen(false); }}
        title={`Force complete match #${repairId}?`}
        description="Marks the match completed and settles the result from its innings as they stand now. Run Recalculate stats afterwards to rebuild awards and player stats."
        loading={busy}
      />
    </div>
  );
};
export default MatchOps;
